/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '@alfresco/adf-core';
import { AlfrescoApiService, ContentNodeSelectorComponent, ContentNodeSelectorComponentData, NodeAction } from '@alfresco/adf-content-services';
import { Node, NodesApi } from '@alfresco/js-api';
import { DateInputComponent, PageHeaderComponent } from '@alfresco/aca-shared';
import { Observable, Subject, forkJoin, from, of } from 'rxjs';
import { catchError, concatMap, map, toArray } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DmsField, DmsMetadataService, DmsOption } from './dms-metadata.service';
import { LocalFilePreviewComponent, countPdfPages } from './local-file-preview.component';
import { OcrService } from '../ocr/ocr.service';
import { OcrKind, OcrOutcome, OcrPipelineService, OcrProgress } from '../ocr/ocr-pipeline.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AssistantService, ClassifyField } from '../assistant/assistant.service';

/** The model fields this form lays out by hand; anything else is rendered generically. */
const KNOWN_FIELDS = new Set([
  'dms:documentCode',
  'dms:documentType',
  'dms:documentDate',
  'dms:department',
  'dms:documentOwner',
  'dms:status',
  'dms:confidentiality'
]);

/** The form's own fields, by the name each one has in `form`. */
type FormField = 'title' | 'documentCode' | 'documentType' | 'documentDate' | 'department' | 'documentOwner' | 'expiryDate' | 'description';

/**
 * The form fields the AI may fill, by the property each one is stored as.
 * Status and confidentiality are absent on purpose: they decide who may read a
 * document and where it is in its life, and stay a person's call.
 */
const SUGGESTIBLE: Record<string, { field: FormField; label: string; dataType: string }> = {
  'cm:title': { field: 'title', label: 'DMS.DOCUMENT_UPLOAD.85B482', dataType: 'd:text' },
  'dms:documentCode': { field: 'documentCode', label: 'DMS.DOCUMENT_UPLOAD.0B315E', dataType: 'd:text' },
  'dms:documentType': { field: 'documentType', label: 'DMS.DOCUMENT_UPLOAD.EA3757', dataType: 'd:text' },
  'dms:documentDate': { field: 'documentDate', label: 'DMS.DOCUMENT_UPLOAD.58A77B', dataType: 'd:date' },
  'dms:department': { field: 'department', label: 'DMS.DOCUMENT_UPLOAD.74B287', dataType: 'd:text' },
  'dms:documentOwner': { field: 'documentOwner', label: 'DMS.DOCUMENT_UPLOAD.B4F2EC', dataType: 'd:text' },
  'cm:to': { field: 'expiryDate', label: 'DMS.DOCUMENT_UPLOAD.F51C27', dataType: 'd:date' },
  'cm:description': { field: 'description', label: 'DMS.DOCUMENT_UPLOAD.9FB99F', dataType: 'd:mltext' }
};

/** How many pages of a file are read for a suggestion; the front says what a document is. */
const SUGGEST_PAGES = 3;

/** A file queued for upload, with the outcome once it has been sent. */
interface QueuedFile {
  file: File;
  state: 'ready' | 'uploading' | 'done' | 'failed';
  error?: string;
  /** What the file needs before its words can be searched; decided from its extension. */
  ocr: OcrKind;
  /** Where reading its text got to, once the file is in the repository. */
  ocrState?: 'reading' | 'done' | 'empty' | 'failed' | 'skipped';
  ocrProgress?: OcrProgress;
  ocrOutcome?: OcrOutcome;
  ocrError?: string;
  /** The document it became, once it is in the repository. */
  nodeId?: string;
  /** Page count once the preview engine has counted it; undefined means "not counted". */
  pages?: number;
  /** Object URL for the queue thumbnail — images only; revoked when the file leaves. */
  previewUrl?: string;
}

/** The line under a queued file that says what happens, or happened, to its text. */
interface TextLine {
  key: string;
  params?: Record<string, unknown>;
  tone: 'plan' | 'reading' | 'done' | 'warn' | 'muted';
  icon: string;
}

const MAX_FILE_BYTES = 100 * 1024 * 1024;
const MAX_DESCRIPTION = 500;
/** How long the filed-files summary stays on screen before it clears itself. */
const BATCH_VISIBLE_MS = 30_000;
/** How long a delete button stays armed for its confirming second press. */
const CONFIRM_DELETE_MS = 3_000;

const ACCEPTED = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.jpg', '.jpeg', '.png', '.bmp', '.gif', '.webp'];

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp']);

/** Badge text per file extension, as in the reference file list. */
const BADGES: Record<string, string> = {
  pdf: 'PDF',
  doc: 'W',
  docx: 'W',
  xls: 'X',
  xlsx: 'X',
  ppt: 'P',
  pptx: 'P',
  txt: 'TXT',
  jpg: 'IMG',
  jpeg: 'IMG',
  png: 'IMG',
  bmp: 'IMG',
  gif: 'IMG',
  webp: 'IMG'
};

/** Wizard position: pick files → review → fill metadata → confirm and file. */
type Step = 1 | 2 | 3 | 4;

/**
 * Document upload / update (UI-01).
 *
 * A four-step flow — choose files, review their content in a local preview,
 * fill the enterprise metadata, then confirm — that sends one or more files of
 * any supported format to a chosen folder and writes the metadata and tags onto
 * every created node. The metadata contract — which fields exist and which
 * values each allows — is read from the `dms:documentInfo` aspect in the
 * repository, so this screen never invents a schema of its own; see
 * {@link DmsMetadataService}.
 *
 * With `?update=<nodeId>` the page becomes "update this document": the node's
 * existing metadata is loaded, a single chosen file becomes a new version
 * through the same `updateNodeContent` mechanism the version history uses, and
 * nothing else about the node moves.
 *
 * Images and scanned PDFs are then read with OCR, so the words on them can be
 * searched — see {@link OcrPipelineService}. That step runs strictly after the
 * file is stored and can only ever add a warning: a missing or broken OCR
 * service never stops a document from being filed.
 */
@Component({
  selector: 'app-document-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    LocalFilePreviewComponent,
    DateInputComponent,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.scss']
})
export class DocumentUploadComponent implements OnInit, OnDestroy {
  private readonly dms = inject(DmsMetadataService);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly ocr = inject(OcrService);
  private readonly pipeline = inject(OcrPipelineService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(AlfrescoApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly assistant = inject(AssistantService);

  readonly accept = ACCEPTED.join(',');
  readonly maxDescription = MAX_DESCRIPTION;
  readonly maxOcrPages = OcrPipelineService.MAX_PAGES;

  /** Whether images and scans are read after upload. On by default: it is what makes them findable. */
  readText = true;

  /** State of the OCR service, asked when the page opens. */
  readonly engine = signal<'checking' | 'ready' | 'starting' | 'unavailable'>('checking');
  private engineTimer?: ReturnType<typeof setTimeout>;

  readonly queue = signal<QueuedFile[]>([]);

  /**
   * What the last upload filed, once it is over. The queue is cleared when a run
   * finishes — left there, the same files sit ready to be sent a second time —
   * so what happened to each of them is kept here instead: where it went, what
   * OCR made of it, and a way to open it.
   */
  readonly lastBatch = signal<QueuedFile[]>([]);
  private batchTimer?: ReturnType<typeof setTimeout>;
  readonly dragging = signal(false);
  readonly busy = signal(false);
  readonly schemaReady = signal(false);
  readonly schemaMissing = signal(false);

  // ---- wizard state ----------------------------------------------------------

  /** Current step: 1 pick files · 2 review · 3 metadata · 4 confirm. */
  readonly step = signal<Step>(1);
  /** Which queued file the preview pane shows. */
  readonly selectedIndex = signal(-1);
  readonly selected = computed(() => this.queue()[this.selectedIndex()] ?? null);

  /** Metadata card tab: the fields this form knows vs whatever the model gained. */
  readonly metaTab = signal<'main' | 'extra'>('main');
  /** The extra options card (OCR / destination / tags) is collapsible. */
  readonly optionsOpen = signal(true);

  /**
   * Set when the reader touched any metadata field — the queue table's delete
   * asks for a confirming press so edited details are not thrown away silently.
   */
  readonly metaDirty = signal(false);
  /** Field errors only appear once the reader tried to move past metadata. */
  readonly touched = signal(false);

  /** Row index whose delete button is armed; -1 = none. */
  readonly confirmDelete = signal(-1);
  private confirmTimer?: ReturnType<typeof setTimeout>;

  // ---- update mode -----------------------------------------------------------

  /** The node being versioned, when opened via `?update=<nodeId>`; null = new upload. */
  readonly updateTarget = signal<Node | null>(null);
  readonly updateMode = computed(() => !!this.updateTarget());
  readonly updateLoading = signal(false);
  /** New version kind — same choice the version history offers. */
  updateMajor = true;
  versionComment = '';

  /** The version the document currently serves, e.g. "1.2". */
  readonly currentVersion = computed(() => String(this.updateTarget()?.properties?.['cm:versionLabel'] ?? ''));
  /** What the new file will be numbered. */
  readonly nextVersion = computed(() => {
    const label = parseFloat(this.currentVersion());
    if (!isFinite(label)) {
      return '';
    }
    return this.updateMajor ? (Math.floor(label) + 1).toFixed(1) : (label + 0.1).toFixed(1);
  });

  /** The defaults the model declared, so a cleared form falls back to them. */
  private statusDefault = '';
  private confidentialityDefault = '';

  readonly tags = signal<string[]>([]);
  tagDraft = '';

  destinationId = '-my-';
  destinationLabel = 'DMS.UI.A4286B';

  /** Selectable values, exactly as the repository defines them. */
  documentTypes: DmsOption[] = [];
  statuses: DmsOption[] = [];
  confidentialities: DmsOption[] = [];

  form = {
    title: '',
    documentCode: '',
    documentType: '',
    documentDate: '',
    department: '',
    documentOwner: '',
    status: '',
    confidentiality: '',
    expiryDate: '',
    description: ''
  };

  /**
   * Whether the AI may suggest metadata. It follows the assistant's own switch,
   * so a company that turned AI off, or only this feature, never sees the button.
   */
  readonly canSuggest = computed(() => !!this.assistant.status()?.classify);
  readonly suggesting = signal(false);
  /** Properties the AI filled, marked until the form is sent or reset. */
  readonly suggested = signal<ReadonlySet<string>>(new Set());
  /** Bumped when the form is reset, so a suggestion still on its way lands nowhere. */
  private formRound = 0;
  /** The type list as the model declares it, to tell the AI which codes it may answer. */
  private typeField?: DmsField;

  /** Fields the model gained after this form was written; rendered generically. */
  readonly extraFields = signal<DmsField[]>([]);
  /** What has been typed into them, keyed by QName. */
  extra: Record<string, string> = {};

  /**
   * The required fields are all answered — gating the confirm step.
   * A method, not a computed: `form` is a plain object, so a computed would
   * freeze on the first read and never notice the fields being filled.
   */
  metadataValid(): boolean {
    return !!(this.form.title.trim() && this.form.documentType && this.form.documentDate && this.form.status);
  }

  readonly canSubmit = computed(() => this.queue().some((q) => q.state === 'ready' || q.state === 'failed') && !this.busy());

  /** The confirm step's summary lines. */
  readonly readyCount = computed(() => this.queue().filter((q) => q.state === 'ready' || q.state === 'failed').length);

  ngOnInit(): void {
    this.checkEngine();
    if (!this.assistant.status()) {
      this.assistant.refreshStatus();
    }
    this.dms.schema().subscribe({
      next: (schema) => {
        if (!schema.available) {
          this.schemaMissing.set(true);
          return;
        }
        const options = (field?: DmsField) => field?.options ?? [];
        this.typeField = schema.fields['dms:documentType'];
        this.documentTypes = options(schema.fields['dms:documentType']);
        this.statuses = options(schema.fields['dms:status']);
        this.confidentialities = options(schema.fields['dms:confidentiality']);
        this.statusDefault = schema.fields['dms:status']?.defaultValue ?? '';
        this.confidentialityDefault = schema.fields['dms:confidentiality']?.defaultValue ?? '';
        this.form.status = this.statusDefault;
        this.form.confidentiality = this.confidentialityDefault;

        // anything an administrator added to the model beyond the fields this
        // form knows by name, so a new field can actually be filled in
        const extra = Object.values(schema.fields).filter((field) => field.id.startsWith('dms:') && !KNOWN_FIELDS.has(field.id));
        this.extraFields.set(extra);
        for (const field of extra) {
          this.extra[field.id] = field.defaultValue ?? '';
        }
        this.schemaReady.set(true);
        // the update target's values land on top of the model defaults
        this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
          const id = params.get('update');
          if (id && this.updateTarget()?.id !== id) {
            this.loadUpdateTarget(id);
          }
        });
      },
      error: () => this.schemaMissing.set(true)
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.engineTimer);
    clearTimeout(this.batchTimer);
    clearTimeout(this.confirmTimer);
    this.revokePreviews(this.queue());
  }

  // ---- wizard ----------------------------------------------------------------

  /** Moves forward when the current step allows it; step 3 validates first. */
  next(): void {
    const step = this.step();
    if (step === 1) {
      if (this.queue().length) {
        this.step.set(2);
      }
      return;
    }
    if (step === 2) {
      this.step.set(3);
      return;
    }
    if (step === 3) {
      this.touched.set(true);
      if (this.metadataValid()) {
        this.step.set(4);
      }
    }
  }

  back(): void {
    this.step.update((s) => (s > 1 ? ((s - 1) as Step) : s));
  }

  /** Step numbers for the indicator; current is primary, future is muted. */
  readonly steps: { n: Step; key: string }[] = [
    { n: 1, key: 'DMS.UPLOAD.STEP_PICK' },
    { n: 2, key: 'DMS.UPLOAD.STEP_REVIEW' },
    { n: 3, key: 'DMS.UPLOAD.STEP_METADATA' },
    { n: 4, key: 'DMS.UPLOAD.STEP_CONFIRM' }
  ];

  stepClass(n: number): string {
    if (n === this.step()) {
      return 'aca-upload__step--current';
    }
    return n < this.step() ? 'aca-upload__step--done' : 'aca-upload__step--todo';
  }

  // ---- update mode -----------------------------------------------------------

  /** Loads the document being versioned and fills the form from its metadata. */
  private loadUpdateTarget(id: string): void {
    this.updateLoading.set(true);
    const nodes = new NodesApi(this.api.getInstance() as any);
    from(nodes.getNode(id, { include: ['properties', 'path'] } as any) as Promise<any>).subscribe({
      next: (res) => {
        const node = res?.entry as Node;
        if (!node?.id) {
          this.updateLoading.set(false);
          return;
        }
        this.updateTarget.set(node);
        this.prefillFromNode(node);
        this.updateLoading.set(false);
      },
      error: () => {
        this.updateLoading.set(false);
        this.notification.showError(this.translate.instant('DMS.UPLOAD.UPDATE_LOAD_FAILED'));
      }
    });
  }

  /**
   * Copies the node's existing metadata into the form. Update keeps what was
   * there — clearing it would quietly erase metadata on save.
   */
  private prefillFromNode(node: Node): void {
    const p = (node.properties ?? {}) as Record<string, any>;
    const day = (value: unknown) => String(value ?? '').slice(0, 10);
    this.form = {
      title: p['cm:title'] ?? String(node.name ?? '').replace(/\.[^.]+$/, ''),
      documentCode: p['dms:documentCode'] ?? '',
      documentType: p['dms:documentType'] ?? '',
      documentDate: day(p['dms:documentDate']),
      department: p['dms:department'] ?? '',
      documentOwner: p['dms:documentOwner'] ?? '',
      status: p['dms:status'] || this.statusDefault,
      confidentiality: p['dms:confidentiality'] || this.confidentialityDefault,
      expiryDate: day(p['cm:to']),
      description: p['cm:description'] ?? ''
    };
    for (const field of this.extraFields()) {
      this.extra[field.id] = p[field.id] != null ? String(p[field.id]) : (field.defaultValue ?? '');
    }
    const parent = node.path?.elements?.[node.path.elements.length - 1];
    if (node.parentId) {
      this.destinationId = node.parentId;
      this.destinationLabel = parent?.name ?? node.parentId;
    }
  }

  /** Leaves update mode entirely. */
  exitUpdate(): void {
    void this.router.navigate(['/upload']);
  }

  // ---- OCR service -----------------------------------------------------------

  /**
   * Asks whether the OCR service is up. It loads its model in the background
   * after it starts, so "starting" is asked again for a minute before giving up.
   */
  checkEngine(attempt = 0): void {
    clearTimeout(this.engineTimer);
    if (attempt === 0) {
      this.engine.set('checking');
    }

    this.ocr.health().subscribe({
      next: (health) => {
        if (health.status === 'ready') {
          this.engine.set('ready');
          return;
        }
        if (health.status === 'starting' && attempt < 20) {
          this.engine.set('starting');
          this.engineTimer = setTimeout(() => this.checkEngine(attempt + 1), 3000);
          return;
        }
        this.engine.set('unavailable');
      },
      error: () => this.engine.set('unavailable')
    });
  }

  engineKey(): string {
    return 'DMS.DOCUMENT_UPLOAD.OCR_ENGINE_' + this.engine().toUpperCase();
  }

  // ---- file selection ----------------------------------------------------------

  onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  triggerFile(input: HTMLInputElement): void {
    input.click();
  }

  /** Shows a queued file in the preview — also the table's eye action. */
  viewFile(index: number): void {
    this.selectedIndex.set(index);
    if (this.step() === 1) {
      this.step.set(2);
    }
  }

  /** The table's edit action: focus that file and open the metadata step. */
  editFile(index: number): void {
    this.selectedIndex.set(index);
    this.metaTab.set('main');
    this.step.set(3);
  }

  /**
   * Removes a queued file. When metadata has been typed, the first press only
   * arms the button — a second press within a few seconds actually removes, so
   * entered details are never discarded by accident.
   */
  removeFile(index: number): void {
    if (this.metaDirty() && this.confirmDelete() !== index) {
      this.confirmDelete.set(index);
      clearTimeout(this.confirmTimer);
      this.confirmTimer = setTimeout(() => this.confirmDelete.set(-1), CONFIRM_DELETE_MS);
      return;
    }
    this.removeAt(index);
  }

  private removeAt(index: number): void {
    this.confirmDelete.set(-1);
    const removed = this.queue()[index];
    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
    }
    this.queue.update((list) => list.filter((_, i) => i !== index));
    const selected = this.selectedIndex();
    if (selected === index) {
      this.selectedIndex.set(Math.min(index, this.queue().length - 1));
    } else if (selected > index) {
      this.selectedIndex.set(selected - 1);
    }
    if (!this.queue().length) {
      this.step.set(1);
    }
  }

  clearQueue(): void {
    this.confirmDelete.set(-1);
    this.revokePreviews(this.queue());
    this.queue.set([]);
    this.selectedIndex.set(-1);
    this.step.set(1);
  }

  private revokePreviews(items: QueuedFile[]): void {
    for (const item of items) {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    }
  }

  private addFiles(files: File[]): void {
    let accepted: QueuedFile[] = [];

    for (const file of files) {
      const ext = this.extension(file.name);
      // The notification translates whatever it is given as one key, so a key
      // with the file name glued on used to reach the screen untranslated.
      if (!ACCEPTED.includes('.' + ext)) {
        this.notification.showWarning(this.translate.instant('DMS.DOCUMENT_UPLOAD.UNSUPPORTED', { name: file.name, ext }));
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        this.notification.showWarning(this.translate.instant('DMS.DOCUMENT_UPLOAD.TOO_LARGE', { name: file.name }));
        continue;
      }
      accepted.push({ file, state: 'ready', ocr: this.pipeline.kindOf(file.name) });
    }

    if (!accepted.length) {
      return;
    }

    // an update is one new version of one document — the latest pick replaces
    this.dismissBatch();
    if (this.updateMode()) {
      this.revokePreviews(this.queue());
      accepted = accepted.slice(-1);
      this.queue.set(accepted);
      this.selectedIndex.set(0);
    } else {
      const firstNew = this.queue().length;
      this.queue.update((list) => [...list, ...accepted]);
      if (this.selectedIndex() < 0) {
        this.selectedIndex.set(firstNew);
      }
    }

    // the first file names the document, matching the reference
    if (!this.form.title && !this.updateMode()) {
      this.form.title = accepted[0].file.name.replace(/\.[^.]+$/, '');
    }

    if (this.step() === 1) {
      this.step.set(2);
    }

    // page counts are read in the background so the table fills in without
    // waiting for the reader to open each file in the preview
    accepted.forEach((item) => {
      if (item.ocr === 'pdf') {
        void countPdfPages(item.file)
          .then((pages) => this.patch(item, { pages }))
          .catch(() => undefined);
      } else if (IMAGE_EXTENSIONS.has(this.extension(item.file.name))) {
        item.pages = 1;
        item.previewUrl = URL.createObjectURL(item.file);
      }
    });
  }

  /** The preview counted this file's pages — keep the table in step with it. */
  onPagesKnown(event: { file: File; pages: number }): void {
    const item = this.queue().find((q) => q.file === event.file);
    if (item) {
      this.patch(item, { pages: event.pages });
    }
  }

  // ---- queue table -----------------------------------------------------------

  /**
   * The badge a row carries. Ready means the file can be sent once the form is
   * answered; incomplete means required metadata is still missing.
   */
  statusOf(item: QueuedFile): 'ready' | 'incomplete' | 'uploading' | 'done' | 'failed' {
    if (item.state === 'done') {
      return 'done';
    }
    if (item.state === 'failed') {
      return 'failed';
    }
    if (item.state === 'uploading' || item.ocrState === 'reading') {
      return 'uploading';
    }
    return this.metadataValid() ? 'ready' : 'incomplete';
  }

  statusKey(item: QueuedFile): string {
    return 'DMS.UPLOAD.STATUS_' + this.statusOf(item).toUpperCase();
  }

  /** File-type label for the table's type column. */
  typeLabel(name: string): string {
    return this.extension(name).toUpperCase() || '—';
  }

  /** Which required field is still unanswered — drives the inline errors. */
  fieldMissing(name: 'title' | 'documentType' | 'documentDate' | 'status'): boolean {
    return this.touched() && !String(this.form[name] ?? '').trim();
  }

  // ---- tags ------------------------------------------------------------------

  addTag(event: Event): void {
    event.preventDefault();
    const value = this.tagDraft.trim();
    if (!value || this.tags().includes(value)) {
      this.tagDraft = '';
      return;
    }
    this.tags.update((list) => [...list, value]);
    this.tagDraft = '';
  }

  removeTag(tag: string): void {
    this.tags.update((list) => list.filter((t) => t !== tag));
  }

  // ---- destination -------------------------------------------------------------

  pickDestination(): void {
    const select = new Subject<Node[]>();
    const data: ContentNodeSelectorComponentData = {
      selectionMode: 'single',
      title: 'DMS.UI.4626D2',
      actionName: NodeAction.CHOOSE,
      currentFolderId: this.destinationId === '-my-' ? undefined : this.destinationId,
      select
    };

    this.dialog.open(ContentNodeSelectorComponent, { data, panelClass: 'adf-content-node-selector-dialog', width: '630px' });

    select.subscribe((selections: Node[]) => {
      const folder = selections?.[0];
      if (folder?.id) {
        this.destinationId = folder.id;
        this.destinationLabel = folder.path?.name ? folder.path.name + ' / ' + folder.name : folder.name;
      }
      select.complete();
    });
  }

  // ---- AI suggestion -----------------------------------------------------------

  /** The file a suggestion reads: the one in the preview, else the first queued. */
  suggestSource(): File | null {
    return this.selected()?.file ?? this.queue()[0]?.file ?? null;
  }

  isSuggested(property: string): boolean {
    return this.suggested().has(property);
  }

  /**
   * Reads the first pages of the file in the preview and asks the AI what its
   * details probably are. Only empty fields are filled (the title counts as empty
   * while it is still the file's own name) and each one is marked: nothing is
   * stored until the person reviews the form and uploads.
   */
  async suggest(): Promise<void> {
    const file = this.suggestSource();
    if (!file || this.suggesting()) {
      return;
    }
    const round = this.formRound;
    this.suggesting.set(true);

    let text = '';
    try {
      // a scan needs the OCR engine; without it there is nothing to read
      const needsEngine = this.pipeline.kindOf(file.name) !== 'none';
      text = !needsEngine || this.engine() === 'ready' ? await this.pipeline.textOf(file, file.name, SUGGEST_PAGES) : '';
    } catch {
      text = '';
    }
    if (round !== this.formRound) {
      return;
    }
    if (!text) {
      this.suggesting.set(false);
      this.notification.showWarning(this.translate.instant('DMS.ASSISTANT.CLASSIFY_NO_TEXT_FILE'));
      return;
    }

    this.assistant.classifyText(file.name, text, this.form.confidentiality, this.suggestFields()).subscribe({
      next: (result) => {
        if (round !== this.formRound) {
          return;
        }
        this.suggesting.set(false);
        this.applySuggestion(file, result.fields ?? {}, result.reason);
      },
      error: (error) => {
        if (round !== this.formRound) {
          return;
        }
        this.suggesting.set(false);
        this.notification.showError(this.translate.instant(error?.status === 403 ? 'DMS.ASSISTANT.CLASSIFY_OFF' : 'DMS.ASSISTANT.CLASSIFY_FAIL'));
      }
    });
  }

  /** What the AI is told it may fill: this form's own fields, then those an administrator added. */
  private suggestFields(): ClassifyField[] {
    const label = (key: string) => this.translate.instant(key);
    const optionsOf = (field?: DmsField) => field?.options?.map((option) => ({ value: option.value, label: label(option.label) }));
    const fields: ClassifyField[] = Object.entries(SUGGESTIBLE).map(([id, entry]) => ({
      id,
      label: label(entry.label),
      dataType: entry.dataType,
      options: id === 'dms:documentType' ? optionsOf(this.typeField) : undefined
    }));
    for (const field of this.extraFields()) {
      fields.push({ id: field.id, label: label(this.fieldLabel(field)), dataType: field.dataType, options: optionsOf(field) });
    }
    return fields;
  }

  private applySuggestion(file: File, values: Record<string, string>, reason?: string | null): void {
    const fileTitle = file.name.replace(/\.[^.]+$/, '');
    const extras = new Set(this.extraFields().map((field) => field.id));
    const filled = new Set<string>();
    let kept = 0;

    for (const [id, value] of Object.entries(values)) {
      const known = SUGGESTIBLE[id];
      if (known) {
        const current = String(this.form[known.field] ?? '').trim();
        if (!current || (known.field === 'title' && current === fileTitle)) {
          this.form[known.field] = known.field === 'description' ? value.slice(0, MAX_DESCRIPTION) : value;
          filled.add(id);
        } else {
          kept++;
        }
      } else if (extras.has(id)) {
        if ((this.extra[id] ?? '').trim()) {
          kept++;
        } else {
          this.extra[id] = value;
          filled.add(id);
        }
      }
    }

    this.suggested.set(new Set([...this.suggested(), ...filled]));
    if (filled.size) {
      this.metaDirty.set(true);
      this.notification.showInfo(this.translate.instant('DMS.ASSISTANT.CLASSIFY_DONE', { filled: filled.size, kept }));
    } else {
      const key = kept ? 'KEPT' : reason === 'no-text' ? 'NO_TEXT_FILE' : reason === 'confidentiality' ? 'WITHHELD' : 'NONE';
      this.notification.showWarning(this.translate.instant('DMS.ASSISTANT.CLASSIFY_' + key));
    }
  }

  // ---- submit ----------------------------------------------------------------

  /** Uploads with the status chosen in the form. */
  submit(): void {
    this.run(this.form.status);
  }

  /** Uploads as a draft — a real `dms:status` value, not a client-only state. */
  saveDraft(): void {
    this.run('draft');
  }

  /** Puts the summary away, for the next batch. */
  dismissBatch(): void {
    clearTimeout(this.batchTimer);
    this.lastBatch.set([]);
  }

  openDocument(nodeId: string | undefined): void {
    if (nodeId) {
      void this.router.navigate(['/document', nodeId]);
    }
  }

  cancel(): void {
    this.clearQueue();
    this.tags.set([]);
    this.tagDraft = '';
    this.metaDirty.set(false);
    this.touched.set(false);
    // update mode resets to the document's own values, not a blank form
    const target = this.updateTarget();
    if (target) {
      this.prefillFromNode(target);
    } else {
      this.resetForm();
    }
  }

  /**
   * Empties the metadata for the next batch. Selects and administrator-added
   * fields go back to the defaults the model declared rather than keeping the
   * last document's values.
   */
  private resetForm(): void {
    this.formRound++;
    this.suggested.set(new Set());
    this.suggesting.set(false);
    this.form = {
      title: '',
      documentCode: '',
      documentType: '',
      documentDate: '',
      department: '',
      documentOwner: '',
      status: this.statusDefault,
      confidentiality: this.confidentialityDefault,
      expiryDate: '',
      description: ''
    };
    for (const field of this.extraFields()) {
      this.extra[field.id] = field.defaultValue ?? '';
    }
  }

  private run(status: string): void {
    const pending = this.queue().filter((q) => q.state === 'ready' || q.state === 'failed');
    if (!pending.length || this.busy()) {
      return;
    }
    this.touched.set(true);
    if (!this.metadataValid()) {
      this.step.set(3);
      return;
    }

    this.busy.set(true);
    const properties = this.buildProperties(status);
    const tags = this.tags();
    const target = this.updateTarget();

    of(...pending)
      .pipe(
        concatMap((item) => {
          this.patch(item, { state: 'uploading', error: undefined, ocrState: undefined, ocrError: undefined });
          // update mode writes a new version onto the existing node instead of
          // creating a second document — same call the version history makes
          const upload$ = target
            ? from(
                new NodesApi(this.api.getInstance() as any).updateNodeContent(
                  target.id,
                  item.file as any,
                  {
                    majorVersion: this.updateMajor,
                    comment: this.versionComment.trim() || undefined
                  } as any
                ) as Promise<any>
              ).pipe(map((res: any) => res?.entry ?? res))
            : this.dms.upload(item.file, this.destinationId);
          return upload$.pipe(
            concatMap((node) =>
              // the binary is already stored, so a metadata or tag failure must
              // never be reported as a failed upload
              forkJoin([this.dms.applyMetadata(node.id, properties), tags.length ? this.dms.addTags(node.id, tags) : of(null)]).pipe(
                map(() => undefined as string | undefined),
                catchError((err) => of(this.describe(err))),
                concatMap((warn) => {
                  // the file is filed; say so while its text is being read
                  this.patch(item, { state: 'done', error: warn, nodeId: node.id });
                  return this.readText$(item, node).pipe(map((ocrWarn) => ({ item, ok: true, warn: warn ?? ocrWarn })));
                })
              )
            ),
            catchError((err) => of({ item, ok: false, warn: this.describe(err) }))
          );
        }),
        toArray()
      )
      .subscribe((results) => {
        this.busy.set(false);

        for (const result of results) {
          if (!result.ok) {
            this.patch(result.item, { state: 'failed', error: result.warn });
          }
        }

        const failed = results.filter((r) => !r.ok).length;
        const warned = results.filter((r) => r.ok && r.warn).length;
        const ok = results.length - failed;

        // What was filed moves to the summary, which clears itself after a
        // while so the page does not sit holding a finished batch. With nothing
        // left to retry the form is emptied for the next batch; with failures
        // it stays, so a retry sends the failed files with the same details.
        const filed = this.queue().filter((q) => q.state === 'done');
        this.showBatch(filed);
        if (failed) {
          this.queue.update((list) => list.filter((q) => q.state !== 'done'));
          this.step.set(2);
        } else {
          this.cancel();
          this.step.set(1);
        }

        const doneKey = target ? 'DMS.UPLOAD.UPDATED_ALL' : 'DMS.DOCUMENT_UPLOAD.DONE_ALL';
        if (failed) {
          this.notification.showError(this.translate.instant('DMS.DOCUMENT_UPLOAD.DONE_SOME_FAILED', { ok, failed }));
        } else if (warned) {
          this.notification.showWarning(this.translate.instant('DMS.DOCUMENT_UPLOAD.DONE_WITH_WARNINGS', { ok }));
        } else {
          // a single filed file gets an "open it" shortcut on the popup itself
          const nodeId = filed.length === 1 ? filed[0].nodeId : undefined;
          const snack = nodeId
            ? this.notification.showInfo(this.translate.instant(doneKey, { ok }), 'DMS.DOCUMENT_UPLOAD.BATCH_OPEN')
            : this.notification.showInfo(this.translate.instant(doneKey, { ok }));
          if (nodeId) {
            snack?.onAction().subscribe(() => this.openDocument(nodeId));
          }
        }
      });
  }

  /** Shows what was just filed, then lets the summary clear itself. */
  private showBatch(items: QueuedFile[]): void {
    clearTimeout(this.batchTimer);
    this.lastBatch.set(items);
    if (items.length) {
      this.batchTimer = setTimeout(() => this.lastBatch.set([]), BATCH_VISIBLE_MS);
    }
  }

  /**
   * Reads the words off a stored file and indexes them. Never fails: whatever
   * goes wrong comes back as the warning to show, because by now the file is
   * already in the repository and the upload itself succeeded.
   */
  private readText$(item: QueuedFile, node: { id: string; name?: string }): Observable<string | undefined> {
    if (item.ocr === 'none' || !this.readText) {
      return of(undefined);
    }
    if (this.engine() !== 'ready') {
      this.patch(item, { ocrState: 'skipped' });
      return of(this.translate.instant('DMS.DOCUMENT_UPLOAD.OCR_SKIPPED'));
    }

    this.patch(item, { ocrState: 'reading', ocrProgress: undefined });
    return this.pipeline
      .readAndIndex(item.file, node.id, node.name ?? item.file.name, (progress) => this.patch(item, { ocrProgress: progress }))
      .pipe(
        map((outcome) => {
          this.patch(item, { ocrState: outcome.read + outcome.fromText ? 'done' : 'empty', ocrOutcome: outcome });
          return undefined;
        }),
        catchError((error) => {
          const reason: string = error?.error?.message ?? error?.message ?? this.translate.instant('DMS.UI.1FB8F8');
          this.patch(item, { ocrState: 'failed', ocrError: reason });
          return of(this.translate.instant('DMS.DOCUMENT_UPLOAD.OCR_FAILED', { reason }));
        })
      );
  }

  /** The line under a queued file saying what happens, or happened, to its text. */
  ocrLine(item: QueuedFile): TextLine {
    const key = (name: string) => 'DMS.DOCUMENT_UPLOAD.' + name;
    const outcome = item.ocrOutcome;

    switch (item.ocrState) {
      case 'reading':
        return item.ocrProgress
          ? { key: key('OCR_READING'), params: { ...item.ocrProgress }, tone: 'reading', icon: 'hourglass_top' }
          : { key: key('OCR_OPENING'), tone: 'reading', icon: 'hourglass_top' };
      case 'done': {
        const pages = (outcome?.read ?? 0) + (outcome?.fromText ?? 0);
        if (outcome && outcome.checked < outcome.total) {
          return {
            key: key('OCR_DONE_TRUNCATED'),
            params: { pages, checked: outcome.checked, total: outcome.total },
            tone: 'warn',
            icon: 'manage_search'
          };
        }
        return item.ocr === 'pdf'
          ? {
              key: key('OCR_DONE_PDF'),
              params: { pages, read: outcome?.read ?? 0, text: outcome?.fromText ?? 0 },
              tone: 'done',
              icon: 'manage_search'
            }
          : { key: key('OCR_DONE'), params: { pages }, tone: 'done', icon: 'manage_search' };
      }
      case 'empty':
        return { key: key('OCR_EMPTY'), tone: 'muted', icon: 'search_off' };
      case 'failed':
        return { key: key('OCR_FAILED'), params: { reason: item.ocrError }, tone: 'warn', icon: 'warning' };
      case 'skipped':
        return { key: key('OCR_SKIPPED'), tone: 'warn', icon: 'warning' };
      default:
        break;
    }

    // not uploaded yet: say what will happen
    if (item.ocr === 'none') {
      return { key: key('OCR_PLAN_NONE'), tone: 'muted', icon: 'text_snippet' };
    }
    if (!this.readText || this.engine() === 'unavailable') {
      return { key: key('OCR_PLAN_OFF'), tone: 'muted', icon: 'block' };
    }
    return { key: key(item.ocr === 'pdf' ? 'OCR_PLAN_PDF' : 'OCR_PLAN_IMAGE'), tone: 'plan', icon: 'document_scanner' };
  }

  private buildProperties(status: string): Record<string, any> {
    // input[type=date] hands back yyyy-MM-dd — already Alfresco's d:date
    // canonical form. Routing it through Date and toISOString used to turn
    // the local midnight into the previous UTC day, storing one day early.
    const day = (value: string) => value || '';

    return {
      'cm:title': this.form.title,
      'cm:description': this.form.description,
      'dms:documentCode': this.form.documentCode,
      'dms:documentType': this.form.documentType,
      'dms:documentDate': day(this.form.documentDate),
      'dms:department': this.form.department,
      'dms:documentOwner': this.form.documentOwner,
      'dms:status': status,
      'dms:confidentiality': this.form.confidentiality,
      // reuses the standard effectivity property the expiry dashboard already reads
      'cm:to': day(this.form.expiryDate),
      ...this.extraProperties(day)
    };
  }

  /** The added fields, each turned into what its data type expects. */
  private extraProperties(day: (value: string) => string): Record<string, any> {
    const out: Record<string, any> = {};
    for (const field of this.extraFields()) {
      const raw = (this.extra[field.id] ?? '').trim();
      if (!raw) {
        continue;
      }
      if (field.dataType === 'd:date' || field.dataType === 'd:datetime') {
        out[field.id] = day(raw);
      } else if (field.dataType === 'd:int' || field.dataType === 'd:long') {
        out[field.id] = Number(raw);
      } else if (field.dataType === 'd:boolean') {
        out[field.id] = raw === 'true';
      } else {
        out[field.id] = raw;
      }
    }
    return out;
  }

  /** The input an added field needs: a list, a date, a number or plain text. */
  inputKind(field: DmsField): 'list' | 'date' | 'number' | 'boolean' | 'text' {
    if (field.options?.length) {
      return 'list';
    }
    if (field.dataType === 'd:date' || field.dataType === 'd:datetime') {
      return 'date';
    }
    if (['d:int', 'd:long', 'd:float', 'd:double', 'd:decimal'].includes(field.dataType)) {
      return 'number';
    }
    return field.dataType === 'd:boolean' ? 'boolean' : 'text';
  }

  /** What the field is called, as the settings screen named it. */
  fieldLabel(field: DmsField): string {
    return DmsMetadataService.label(field.id);
  }

  private patch(item: QueuedFile, changes: Partial<QueuedFile>): void {
    this.queue.update((list) => list.map((q) => (q.file === item.file ? { ...q, ...changes } : q)));
  }

  // ---- view helpers ------------------------------------------------------------

  badge(name: string): string {
    return BADGES[this.extension(name)] ?? 'FILE';
  }

  badgeClass(name: string): string {
    return 'aca-upload__badge--' + this.extension(name);
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  }

  private extension(name: string): string {
    return name.split('.').pop()?.toLowerCase() ?? '';
  }

  private describe(error: any): string {
    return error?.error?.error?.briefSummary || error?.message || this.translate.instant('DMS.UI.1FB8F8');
  }
}
